import { transactionService } from '@/services/TransactionService';
import { entityService } from '@/services/EntityService';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import {
  buildInventoryTrendsReportHtml,
  type InventoryTrendsItem,
  type InventoryTrendsTemplateOptions,
} from '@/reports/templates/inventoryTrendsTemplate';

const coerceString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const toSeconds = (value: string | null): number | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return Math.floor(date.getTime() / 1000);
};

export const runInventoryTrendsReport = async (params: Record<string, unknown>): Promise<void> => {
  const fromDate = (params.fromDate as string | undefined) ?? '';
  const toDate = (params.toDate as string | undefined) ?? '';

  const records = await transactionService.listLatest(1000);

  const fromTs = fromDate ? toSeconds(fromDate) : null;
  const toTs = toDate ? toSeconds(toDate) : null;
  const nowTs = Date.now() / 1000;
  const thirtyDaysAgoTs = nowTs - 30 * 24 * 3600;

  const filtered = records.filter((record) => {
    const processedTs = toSeconds(record.processedAt);
    if (processedTs == null) {
      return false;
    }
    if (fromTs != null && processedTs < fromTs) return false;
    if (toTs != null && processedTs > toTs + 24 * 3600 - 1) return false;
    return true;
  });

  const byKey = new Map<
    string,
    {
      lin: string | null;
      nsn: string | null;
      nomenclature: string;
      totalCount: number;
      prev30Count: number;
      firstTs: number;
      lastTs: number;
    }
  >();

  for (const record of filtered) {
    const processedTs = toSeconds(record.processedAt);
    if (processedTs == null) continue;

    const lin = record.assetLin ?? null;
    const nsn = record.assetNsn ?? null;
    const nomen = record.assetNomen ?? '—';
    // Group by lin|nsn without nomen so that the same NSN is consolidated
    // regardless of historical size suffixes in stored transaction nomenclature.
    const key = `${lin ?? ''}|${nsn ?? ''}`;

    let entry = byKey.get(key);
    if (!entry) {
      entry = {
        lin,
        nsn,
        nomenclature: nomen,
        totalCount: 0,
        prev30Count: 0,
        firstTs: processedTs,
        lastTs: processedTs,
      };
      byKey.set(key, entry);
    }

    entry.totalCount += 1;
    if (processedTs >= thirtyDaysAgoTs && processedTs <= nowTs) {
      entry.prev30Count += 1;
    }
    if (processedTs < entry.firstTs) entry.firstTs = processedTs;
    if (processedTs > entry.lastTs) entry.lastTs = processedTs;
  }

  // Calculate the period in months based on the report date range
  let reportPeriodMonths = 1; // default to 1 month if we can't determine
  if (fromTs != null && toTs != null) {
    const reportPeriodSeconds = toTs - fromTs;
    reportPeriodMonths = reportPeriodSeconds / (30 * 24 * 3600);
    // Ensure at least some minimum period to avoid division issues
    if (reportPeriodMonths < 0.1) reportPeriodMonths = 0.1;
  }

  const items: InventoryTrendsItem[] = [];

  // Lookup canonical nomenclature from the current catalog by LIN so that the
  // report always shows the clean base name (e.g. "CAP, PATROL OCP") regardless
  // of any size suffix stored in historical transaction records.
  const uniqueLins = Array.from(
    new Set(
      Array.from(byKey.values())
        .map((e) => e.lin?.trim().toUpperCase())
        .filter((lin): lin is string => Boolean(lin)),
    ),
  );

  const nomenByLin = new Map<string, string>();
  if (uniqueLins.length > 0) {
    for (const batchLins of chunk(uniqueLins, 50)) {
      const linFilter =
        batchLins.length === 1 ? `eq.${batchLins[0]}` : `in.(${batchLins.join(',')})`;
      const catalogs = await entityService.list({
        type: 'catalog',
        limit: batchLins.length * 5,
        order: 'id.asc',
        filters: {
          'entity_data->>lin': linFilter,
          select: 'id,display_name,entity_data',
        },
      });
      for (const cat of catalogs) {
        const entityData = (cat.entity_data ?? {}) as Record<string, unknown>;
        const lin = coerceString(entityData.lin)?.trim().toUpperCase();
        if (!lin || nomenByLin.has(lin)) continue;
        const canonicalName =
          coerceString(cat.display_name) ?? coerceString(entityData.name) ?? null;
        if (canonicalName) {
          nomenByLin.set(lin, canonicalName);
        }
      }
    }
  }

  for (const entry of byKey.values()) {
    const avgMonthly = entry.totalCount / reportPeriodMonths;

    let trendPercent: number | null = null;
    if (avgMonthly > 0) {
      trendPercent = ((entry.prev30Count - avgMonthly) / avgMonthly) * 100;
    }

    items.push({
      lin: entry.lin,
      nsn: entry.nsn,
      // Prefer the catalog's current clean display_name over the stored
      // transaction nomen, which may include a size suffix from before the
      // strip-size-from-nomenclature data migration.
      nomenclature: nomenByLin.get(entry.lin?.trim().toUpperCase() ?? '') ?? entry.nomenclature,
      totalQtyIssued: entry.totalCount,
      avgMonthlyIssued: avgMonthly,
      prev30DaysIssued: entry.prev30Count,
      trendPercent,
    });
  }

  const options: InventoryTrendsTemplateOptions = {
    generatedAt: new Date(),
    header: {
      fromDate,
      toDate,
    },
    items,
  };

  const html = buildInventoryTrendsReportHtml(options);
  outputReport(
    html,
    'inventory-trends-report.html',
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
